import { useMutation, useQueryClient } from '@tanstack/react-query';

import { creditsKeys } from '#creditFeature/models/credits/keys';
import { forceSyncCapabilities } from '#lib/authToken';
import { captureException } from '#lib/sentry';

import {
	inviteMember,
	removeMember,
	setMemberLimits,
	changeMemberRole,
	acceptInvite,
	declineInvite,
	resendInvite,
	revokeInvite,
} from '../../api/orgService';

import { orgKeys } from './keys';

export function useInviteMember() {
	const queryClient = useQueryClient();

	return useMutation({
		meta: { suppressGlobalError: true },
		mutationFn: ({ email, role }) => inviteMember(email, role),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: orgKeys.members() });
			queryClient.invalidateQueries({ queryKey: orgKeys.info() });
		},
		onError: (error, variables) => {
			captureException(error, {
				tags: { section: 'org', operation: 'invite-member' },
				extra: { email: variables.email, role: variables.role },
				level: 'error',
			});
		},
	});
}

export function useRemoveMember() {
	const queryClient = useQueryClient();

	return useMutation({
		meta: { suppressGlobalError: true },
		mutationFn: ({ userId, transferData }) => removeMember(userId, transferData),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: orgKeys.members() });
			queryClient.invalidateQueries({ queryKey: orgKeys.info() });
			queryClient.invalidateQueries({ queryKey: orgKeys.credits() });
		},
		onError: (error, variables) => {
			captureException(error, {
				tags: { section: 'org', operation: 'remove-member' },
				extra: { userId: variables.userId },
				level: 'error',
			});
		},
	});
}

export function useAcceptInvite() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (inviteId) => acceptInvite(inviteId),
		onSuccess: async () => {
			// The org claim is minted into the JWT; with the old token /me still answers 403. Org info
			// must resolve before the invite list empties, or the login self-heal (no org, no invite)
			// would provision a second, own org for the invitee.
			await forceSyncCapabilities();
			await queryClient.refetchQueries({ queryKey: orgKeys.info() });
			await queryClient.invalidateQueries({ queryKey: orgKeys.all });
		},
		onError: (error, inviteId) => {
			captureException(error, {
				tags: { section: 'org', operation: 'accept-invite' },
				extra: { inviteId },
				level: 'error',
			});
		},
	});
}

export function useDeclineInvite() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (inviteId) => declineInvite(inviteId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: orgKeys.pendingInvites() });
		},
		onError: (error, inviteId) => {
			captureException(error, {
				tags: { section: 'org', operation: 'decline-invite' },
				extra: { inviteId },
				level: 'error',
			});
		},
	});
}

export function useResendInvite() {
	const queryClient = useQueryClient();

	return useMutation({
		meta: { suppressGlobalError: true },
		mutationFn: (inviteId) => resendInvite(inviteId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: orgKeys.members() });
		},
		onError: (error, inviteId) => {
			captureException(error, {
				tags: { section: 'org', operation: 'resend-invite' },
				extra: { inviteId },
				level: 'error',
			});
		},
	});
}

export function useRevokeInvite() {
	const queryClient = useQueryClient();

	return useMutation({
		meta: { suppressGlobalError: true },
		mutationFn: (inviteId) => revokeInvite(inviteId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: orgKeys.members() });
		},
		onError: (error, inviteId) => {
			captureException(error, {
				tags: { section: 'org', operation: 'revoke-invite' },
				extra: { inviteId },
				level: 'error',
			});
		},
	});
}

export function useSetMemberLimits() {
	const queryClient = useQueryClient();

	return useMutation({
		meta: { suppressGlobalError: true },
		mutationFn: ({ userId, companyLimit, contactLimit }) => setMemberLimits(userId, { companyLimit, contactLimit }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: orgKeys.members() });
			queryClient.invalidateQueries({ queryKey: orgKeys.credits() });
			queryClient.invalidateQueries({ queryKey: creditsKeys.wallet() });
		},
		onError: (error, variables) => {
			captureException(error, {
				tags: { section: 'org', operation: 'set-member-limits' },
				extra: { userId: variables.userId },
				level: 'error',
			});
		},
	});
}

export function useChangeMemberRole() {
	const queryClient = useQueryClient();

	return useMutation({
		meta: { suppressGlobalError: true },
		mutationFn: ({ userId, role }) => changeMemberRole(userId, role),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: orgKeys.members() });
		},
		onError: (error, variables) => {
			captureException(error, {
				tags: { section: 'org', operation: 'change-member-role' },
				extra: { userId: variables.userId, role: variables.role },
				level: 'error',
			});
		},
	});
}
